import { View } from 'foundations-components/components/View';
import { HsocBodyBackgroundStyles } from 'pricing-pages-ui-components/stylesheets/HsocBodyBackground.styles';
import { useMediaBySize } from 'pricing-pages-ui-components/hooks/useMedia';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import HsocMerchCardGrid from 'pricing-pages-ui-hsoc/components/HsocMerchCardGrid';
import HsocMerchCardGridSkeleton from 'pricing-pages-ui-hsoc/components/HsocMerchCardGridSkeleton';
import { HsocV2PageHero } from 'pricing-pages-ui-hsoc/components/HsocV2PageHero';
import HsocCreditsExplainerSection from '../../components/hsoc-credits-explainer/HsocCreditsExplainerSection';
import HsocFaqsSection from '../../components/hsoc-faqs/HsocFaqsSection';
import HelpMeChooseInline from 'pricing-pages-ui-hsoc/components/plan-quiz/HelpMeChooseInline';
import { PlanQuizProvider } from 'pricing-pages-ui-hsoc/components/plan-quiz/PlanQuizContext';
import { PlanQuizDataProvider } from 'pricing-pages-ui-hsoc/components/plan-quiz/PlanQuizDataContext';
import { useHsocPublicPricing } from './useHsocPublicPricing';
import { useHsocPublicTierOptions } from './useHsocPublicTierOptions';
import { HsocV2PublicPurchaseMotionContainer } from './HsocV2PublicPurchaseMotionContainer';
import { isHsocTierName } from './hsocPublicTiers';
import { HUBSPOT_ON_CREDITS_PAGE } from '../../components/error-boundary/ErrorBoundaryNames';
import { PublicPageWrapper } from '../../components/PublicPageWrapper';
import { useCurrency } from '../../context/CurrencyContext';
import { usePublicPageUrlState } from '../../hooks/usePublicPageUrlState';
import { createDeferredLoadable } from '../../utils/createDeferredLoadable';
import { HSOC_V2_SCREEN, TRACKING_SOURCES, trackHsocPlanQuizEvent, trackHsocV2PageView, trackTermSelectorInteraction } from '../../utils/tracking';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LoadablePlanQuizModal = createDeferredLoadable(() => import( /* webpackChunkName: "plan-quiz-modal" */'pricing-pages-ui-hsoc/components/plan-quiz/PlanQuizModal').then(m => ({
  default: m.default
})));

/* The feature table is kept out of the initial public bundle: it is a large,
 * below-the-fold section that most visitors never expand. */
const LoadablePublicHsocFeatureTable = createDeferredLoadable(() => import( /* webpackChunkName: "public-hsoc-feature-table" */'./feature-table/PublicHsocFeatureTable').then(m => ({
  default: m.default
})));
function HsocPublicPageContent() {
  const isMd = useMediaBySize('md');
  const {
    displayedCurrencyCode
  } = useCurrency();
  const {
    selectedTerm,
    setSelectedTerm
  } = usePublicPageUrlState();

  /* The tier-options query is owned here (fetched once, shared with the cards
   * and the feature table), so its error is thrown in one place and both
   * surfaces read the same tier data + billing term. */
  const tierOptions = useHsocPublicTierOptions(displayedCurrencyCode);
  const {
    cards,
    showTermToggle
  } = useHsocPublicPricing(tierOptions, displayedCurrencyCode, selectedTerm);
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const handleTermChange = useCallback(term => {
    setSelectedTerm(term);
    trackTermSelectorInteraction({
      action: 'click',
      term,
      trackingSource: TRACKING_SOURCES.MERCH_CARD,
      screen: HSOC_V2_SCREEN
    });
  }, [setSelectedTerm]);
  const openQuiz = useCallback(() => setIsQuizOpen(true), []);
  const closeQuiz = useCallback(() => setIsQuizOpen(false), []);
  const planQuizData = useMemo(() => {
    var _tierOptions$products;
    return {
      productsByTier: (_tierOptions$products = tierOptions.productsByTier) !== null && _tierOptions$products !== void 0 ? _tierOptions$products : null,
      currencyCode: displayedCurrencyCode,
      selectedTerm,
      track: trackHsocPlanQuizEvent,
      designSystem: 'creative',
      renderPurchaseMotions: ({
        tierName,
        productApiName,
        motions,
        seatsQuantity
      }) => isHsocTierName(tierName) ? /*#__PURE__*/_jsx(HsocV2PublicPurchaseMotionContainer, {
        tierName: tierName,
        productApiName: productApiName,
        motions: motions,
        currencyCode: displayedCurrencyCode,
        term: selectedTerm,
        trackingSource: TRACKING_SOURCES.PLAN_QUIZ_MODAL,
        seatsQuantity: seatsQuantity
      }) : null
    };
  }, [tierOptions.productsByTier, displayedCurrencyCode, selectedTerm]);
  if (tierOptions.error) throw tierOptions.error;
  const showCards = !tierOptions.loading && cards.length > 0;
  return /*#__PURE__*/_jsxs(PlanQuizDataProvider, {
    value: planQuizData,
    children: [/*#__PURE__*/_jsx(HsocV2PageHero, {}), tierOptions.loading && /*#__PURE__*/_jsx(View, {
      padding: isMd ? '800' : '400',
      children: /*#__PURE__*/_jsx(HsocMerchCardGridSkeleton, {
        showHeader: true
      })
    }), showCards && /*#__PURE__*/_jsx(View, {
      padding: isMd ? '800' : '400',
      children: /*#__PURE__*/_jsx("div", {
        id: "hsoc-tier-cards",
        children: /*#__PURE__*/_jsx(HsocMerchCardGrid, {
          cards: cards,
          showTermToggle: showTermToggle,
          activeTerm: selectedTerm,
          onTermChange: handleTermChange,
          helpMeChoose: /*#__PURE__*/_jsx(HelpMeChooseInline, {
            onOpenPlanQuiz: openQuiz
          })
        })
      })
    }), /*#__PURE__*/_jsx(LoadablePublicHsocFeatureTable, {
      tierOptions: tierOptions,
      currencyCode: displayedCurrencyCode,
      selectedTerm: selectedTerm
    }), /*#__PURE__*/_jsx(View, {
      as: "section",
      paddingInline: isMd ? '800' : '400',
      marginBlockStart: "1100",
      children: /*#__PURE__*/_jsx(HsocCreditsExplainerSection, {})
    }), /*#__PURE__*/_jsx(View, {
      as: "section",
      padding: isMd ? '800' : '400',
      marginBlockStart: "1400",
      marginBlockEnd: "1400",
      children: /*#__PURE__*/_jsx(HsocFaqsSection, {})
    }), isQuizOpen && /*#__PURE__*/_jsx(LoadablePlanQuizModal, {
      onClose: closeQuiz
    })]
  });
}
export default function PublicHsocPricingPage() {
  const hasTrackedPageViewRef = useRef(false);
  useEffect(() => {
    if (!hasTrackedPageViewRef.current) {
      hasTrackedPageViewRef.current = true;
      trackHsocV2PageView();
    }
  }, []);
  return /*#__PURE__*/_jsxs(PublicPageWrapper, {
    errorBoundaryName: HUBSPOT_ON_CREDITS_PAGE,
    navMarkerName: "HSOC_V2_PAGE",
    metricsRoute: "hubspot-credits",
    chunkName: "public-hsoc-pricing-page",
    children: [/*#__PURE__*/_jsx(HsocBodyBackgroundStyles, {}), /*#__PURE__*/_jsx(PlanQuizProvider, {
      children: /*#__PURE__*/_jsx(HsocPublicPageContent, {})
    })]
  });
}